/**
 * Аптеки сети. Самовывоз идёт из аптеки, где товар есть в наличии, доставка —
 * из ближайшей к клиенту (В-16). Закрытая аптека не участвует ни в том, ни в
 * другом, но остаётся в истории заказов.
 */
import type { DayHours, Store } from './api'

export const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'] as const

export const ALL_DAY = 'Круглосуточно'

/** «08:00 – 22:00»; закрытый день — «выходной». */
export function dayText(day: DayHours | null): string {
  return day ? `${day.open} – ${day.close}` : 'выходной'
}

export interface WeekRow {
  day: string
  hours: string
}

/** Расписание строками: семь дней даже у круглосуточной аптеки. */
export function weekRows(store: Store): WeekRow[] {
  return WEEKDAYS.map((day, i) => ({
    day,
    hours: store.is247 ? ALL_DAY : dayText(store.hours[i] ?? null),
  }))
}

/**
 * Режим работы одной строкой. Если все рабочие дни идут по одним часам,
 * показываем их; иначе отправляем читателя на вкладку с расписанием —
 * «08:00 – 22:00, кроме Сб» врало бы о воскресенье.
 */
export function hoursText(store: Store): string {
  if (store.is247) return ALL_DAY
  const open = store.hours.filter((day): day is DayHours => day !== null)
  const [first] = open
  if (!first) return 'закрыта всю неделю'
  const text = dayText(first)
  const same = open.every((day) => dayText(day) === text)
  return same && open.length === 7 ? text : 'по дням недели'
}

/**
 * Как назвать аптеку на экране — в заголовке, списках, выборе и тостах.
 * Название, а не район: в одном районе бывает несколько аптек. Название
 * пустое — тогда район, чтобы строка не осталась без подписи.
 */
export function storeTitle(store: Pick<Store, 'name' | 'district'>): string {
  return store.name.trim() || store.district
}

/** Подпись аптеки по номеру; аптеки нет в списке — `fallback`. */
export function storeTitleById(
  stores: Pick<Store, 'id' | 'name' | 'district'>[] | undefined,
  id: number,
  fallback: string,
): string {
  const store = stores?.find((s) => s.id === id)
  return store ? storeTitle(store) : fallback
}

/** Поиск по названию, району, адресу и заведующему. */
export function filterStores(stores: Store[], query: string): Store[] {
  const q = query.toLowerCase().trim()
  if (!q) return stores
  return stores.filter((s) =>
    [s.name, s.district, s.address, s.manager].some((field) => field.toLowerCase().includes(q)),
  )
}

/**
 * Новая аптека из трёх обязательных полей. Остальное — значения, при которых
 * она ничего не делает: выключена, неделя закрыта, только самовывоз. Так в
 * неё не пойдут заказы, пока её не заполнят в карточке.
 */
export function newStore(fields: { name: string; district: string; address: string }): Store {
  return {
    id: 0,
    name: fields.name.trim(),
    district: fields.district.trim(),
    address: fields.address.trim(),
    metro: '',
    phone: '',
    manager: '',
    lat: null,
    lng: null,
    is247: false,
    hours: [null, null, null, null, null, null, null],
    acceptsPickup: true,
    acceptsDelivery: false,
    dispensesRx: false,
    active: false,
    openNow: false,
    ordersCount: 0,
    fomSyncedAt: 0,
    createdAt: 0,
  }
}

/** Почему аптеку не удалили: к ней привязаны сотрудники (409 `in_use`, R-63). */
export const STORE_IN_USE =
  'К аптеке привязаны сотрудники — переведите их в другую аптеку или на всю сеть'

/** Куда SITE.exe шлёт остатки: строка `[SERVER] URL=` в его SETUP.ini. */
export const FOM_STOCK_PATH = '/api/v1/integrations/fom/stock'

/** SITE.exe шлёт остатки каждые 15 минут; час тишины — повод проверить программу. */
export const FOM_STOCK_STALE_MS = 60 * 60 * 1000

/** Остатки давно не приходили. Ни разу не приходили — не «давно», а «ещё нет». */
export function fomStockStale(lastStock: number, now: number): boolean {
  return lastStock > 0 && now - lastStock > FOM_STOCK_STALE_MS
}

export interface FomHandoff {
  storeName: string
  /** Адрес кассы по https; пусто — в этой сборке адрес не задан. */
  origin: string
  /** Адрес кассы без TLS; пусто — не задан. */
  plainOrigin: string
  tillLogin: string
  tillPassword: string
  siteToken: string
}

/**
 * Доступы одним текстом — техник FOM вставит его себе и впишет в программы.
 * Строки адресов, которых в сборке нет, опускаются, а не печатаются пустыми.
 */
export function fomHandoffText(h: FomHandoff): string {
  const lines = [`Аптека: ${h.storeName}`]
  if (h.origin) lines.push(`Касса (CASHBACK.exe): ${h.origin}`)
  // Без TLS — только для кассы, которая не умеет https: на проде этот порт открывают вручную.
  if (h.plainOrigin) lines.push(`Касса без TLS, только если не умеет https: ${h.plainOrigin}`)
  lines.push(`Логин: ${h.tillLogin}`, `Пароль: ${h.tillPassword}`, 'SITE.exe → SETUP.ini:')
  if (h.origin) lines.push(`  [SERVER] URL=${h.origin}${FOM_STOCK_PATH}`)
  lines.push(`  TOCING=${h.siteToken}`)
  return lines.join('\n')
}
